import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import SubmissionDetail from "@/components/SubmissionDetail";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Trophy, ChevronRight, ChevronLeft } from "lucide-react";

export default function ExamModal({ examId, open, onClose, onSubmitted }) {
  const [exam, setExam] = useState(null);
  const [step, setStep] = useState(0);
  const [mcq, setMcq] = useState({});
  const [subj, setSubj] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !examId) return;
    setExam(null); setStep(0); setMcq({}); setSubj({}); setResult(null);
    setLoading(true);
    api.get(`/exams/${examId}`).then((r) => setExam(r.data)).catch(() => toast.error("Failed to load exam")).finally(() => setLoading(false));
  }, [open, examId]);

  const questions = exam?.questions_json || [];
  const current = questions[step];
  const answered = (q) => (q?.type === "mcq" ? mcq[q.id] != null : (subj[q.id] || "").trim().length > 0);
  const answeredCount = questions.filter(answered).length;
  const progress = questions.length ? (answeredCount / questions.length) * 100 : 0;

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post("/exams/submit", { exam_id: examId, mcq_answers: mcq, subjective_answers: subj });
      setResult(r.data);
      onSubmitted && onSubmitted(r.data);
    } catch (e) { toast.error("Submission failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0E1420] border-[#1E293B] text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="exam-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{exam?.title || "Exam"}</DialogTitle>
        </DialogHeader>

        {loading && <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>}

        {!loading && result && (
          <div className="py-2" data-testid="exam-result">
            <div className="flex flex-col items-center text-center py-4">
              <div className="w-20 h-20 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mb-3">
                <Trophy className="w-9 h-9 text-emerald-400" />
              </div>
              <div className="text-4xl font-extrabold font-display text-emerald-400">{result.percentage}%</div>
              <div className="text-slate-400 text-sm mt-1">{result.score} / {result.max_score} points</div>
              <div className="text-xs text-emerald-500/70 mt-1">A copy has been emailed to your teachers.</div>
            </div>
            <SubmissionDetail submission={result} />
            <button data-testid="exam-close-button" onClick={onClose} className="mt-5 w-full bg-emerald-500 text-emerald-950 font-semibold rounded-xl py-3 hover:bg-emerald-400 transition-colors">Done</button>
          </div>
        )}

        {!loading && !result && current && (
          <div className="py-2">
            <Progress value={progress} className="h-1.5 mb-5 bg-[#1E293B]" />
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs uppercase tracking-wider text-emerald-400 font-semibold">Question {step + 1} of {questions.length}</span>
              <span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${current.type === "mcq" ? "bg-indigo-500/15 text-indigo-400" : "bg-amber-500/15 text-amber-400"}`}>
                {current.type === "mcq" ? "Multiple choice" : `Written · ${current.points} pts`}
              </span>
            </div>
            <div className="text-lg font-medium text-slate-100 mb-5">{current.question}</div>

            {current.type === "mcq" ? (
              <div className="space-y-2.5">
                {current.options.map((opt, idx) => {
                  const active = mcq[current.id] === idx;
                  return (
                    <button key={idx} data-testid={`exam-option-${idx}`} onClick={() => setMcq((a) => ({ ...a, [current.id]: idx }))}
                      className={`w-full text-left px-4 py-3 rounded-xl border transition-all duration-150 ${active ? "bg-emerald-500/15 border-emerald-500 text-emerald-100" : "bg-[#111726] border-[#1E293B] text-slate-300 hover:border-[#334155]"}`}>
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-[#090D16] border border-[#1E293B] text-xs font-semibold mr-3">{String.fromCharCode(65 + idx)}</span>
                      {opt}
                    </button>
                  );
                })}
              </div>
            ) : (
              <Textarea data-testid="exam-subjective-input" value={subj[current.id] || ""} onChange={(e) => setSubj((a) => ({ ...a, [current.id]: e.target.value }))}
                rows={6} placeholder="Write your answer here…" className="bg-[#111726] border-[#1E293B] text-slate-100 focus:border-emerald-500 resize-none" />
            )}

            <div className="flex justify-between items-center mt-6">
              <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
                className="inline-flex items-center gap-1 text-sm text-slate-400 disabled:opacity-30 hover:text-slate-200 transition-colors">
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
              {step < questions.length - 1 ? (
                <button data-testid="exam-next-button" onClick={() => setStep((s) => s + 1)}
                  className="inline-flex items-center gap-1.5 bg-[#111726] border border-[#1E293B] text-slate-200 rounded-xl px-5 py-2.5 hover:border-emerald-500/50 transition-colors">
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button data-testid="submit-exam-button" onClick={submit} disabled={submitting}
                  className="inline-flex items-center gap-2 bg-emerald-500 text-emerald-950 font-semibold rounded-xl px-6 py-2.5 disabled:opacity-40 hover:bg-emerald-400 transition-colors">
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Submit Exam
                </button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
